/**
 * Tela /users (TH-53165 / M1.E4.S4.3).
 *
 * - Tabela: nome, email, role, ativo, último login
 * - Form de criação com Select de role (filtrado pelo role do caller)
 * - Botão "Resetar senha" → POST /users/:id/reset-password (mostra
 *   reset_url no modal até M1.E5 EmailSender entregar)
 * - Toggle ativo: DELETE (soft) / PATCH active=true
 *
 * Mutações no próprio user logado bloqueadas no backend (409): UI
 * esconde toggle e reset pra evitar 409 silencioso.
 *
 * Refatorado em TH-54207 para usar <DataTable> com header mono + status pill
 * (padrão painel-screens.jsx:179-196).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import {
  FilterPills,
  type FilterPillOption,
} from "@/components/ui/FilterPills";
import { Pagination } from "@/components/ui/Pagination";
import { useAuth } from "@/hooks/useAuth";
import { CreateUserDialog } from "@/pages/users/CreateUserDialog";
import { ResetPasswordDialog } from "@/pages/users/ResetPasswordDialog";

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  active: boolean;
  last_login_at: string | null;
}

interface PagedResponse {
  data: User[];
  meta: { page: number; per_page: number; total: number };
}

const PER_PAGE = 20;

const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

async function fetchUsers(
  page: number,
  includeInactive: boolean,
): Promise<PagedResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(PER_PAGE),
  });
  if (includeInactive) params.set("include_inactive", "true");
  const res = await fetch(`/api/users?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedResponse;
}

async function deactivateUser(id: string): Promise<void> {
  const res = await fetch(`/api/users/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

async function reactivateUser(id: string): Promise<void> {
  const res = await fetch(`/api/users/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ active: true }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

type StatusFilter = "all" | "active" | "inactive";

const STATUS_FILTER_OPTIONS: readonly FilterPillOption<StatusFilter>[] = [
  { label: "Todos", value: "all" },
  { label: "Ativos", value: "active" },
  { label: "Inativos", value: "inactive" },
];

export function UsersPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [creating, setCreating] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);

  // Backend retorna só ativos por padrão; include_inactive=true devolve todos.
  // Client filtra "inactive" sobre essa amostra.
  const includeInactive = statusFilter !== "active";

  const canManage =
    user?.role === "super_admin" || user?.role === "tenant_admin";

  const query = useQuery({
    queryKey: ["users", page, includeInactive],
    queryFn: () => fetchUsers(page, includeInactive),
    staleTime: 30_000,
    enabled: canManage,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? reactivateUser(id) : deactivateUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  if (user && !canManage) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CONTA / USUÁRIOS" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Apenas super_admin ou tenant_admin podem gerenciar usuários.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const headerActions = (
    <Button onClick={() => setCreating(true)}>Novo usuário</Button>
  );

  const columns: DataTableColumn<User>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (u) => <span className="font-medium">{u.name ?? "—"}</span>,
    },
    {
      key: "email",
      header: "Email",
      mono: true,
      cell: (u) => (
        <span className="text-[var(--color-muted-foreground)]">{u.email}</span>
      ),
    },
    {
      key: "role",
      header: "Role",
      cell: (u) => (
        <span className="text-[var(--color-muted-foreground)]">{u.role}</span>
      ),
    },
    {
      key: "active",
      header: "Status",
      cell: (u) => (
        <StatusPill tone={u.active ? "success" : "muted"}>
          {u.active ? "Ativo" : "Inativo"}
        </StatusPill>
      ),
    },
    {
      key: "last_login_at",
      header: "Último login",
      align: "right",
      mono: true,
      cell: (u) => (
        <span className="text-[var(--color-muted-foreground)]">
          {u.last_login_at
            ? dateTimeFmt.format(new Date(u.last_login_at))
            : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (u) => {
        const isSelf = u.id === user?.id;
        if (isSelf) {
          return (
            <span className="text-xs text-[var(--color-muted-foreground)]">
              (você)
            </span>
          );
        }
        return (
          <div className="flex justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setResetTarget(u)}
              disabled={!u.active}
              title={
                !u.active ? "Usuário inativo não recebe reset" : undefined
              }
            >
              Resetar senha
            </Button>
            {u.active ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-[var(--color-destructive)]"
                disabled={toggleActive.isPending}
                onClick={() =>
                  toggleActive.mutate({ id: u.id, active: false })
                }
              >
                Desativar
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={toggleActive.isPending}
                onClick={() => toggleActive.mutate({ id: u.id, active: true })}
              >
                Reativar
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const rawRows = query.data?.data ?? [];
  const rows =
    statusFilter === "inactive" ? rawRows.filter((u) => !u.active) : rawRows;
  const subtitle = query.data
    ? `${query.data.meta.total} ${query.data.meta.total === 1 ? "usuário" : "usuários"}`
    : undefined;

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="CONTA / USUÁRIOS"
        title="Usuários"
        actions={headerActions}
      >
        <div className="mx-auto max-w-6xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Usuários do tenant.
          </p>

          <div className="mt-4">
            <FilterPills<StatusFilter>
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(v) => {
                setPage(1);
                setStatusFilter(v);
              }}
            />
          </div>

          {query.error ? (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar
            </div>
          ) : null}

          <div className="mt-4">
            <DataTable<User>
              columns={columns}
              rows={rows}
              title="Usuários"
              subtitle={subtitle}
              rowKey={(u) => u.id}
              emptyMessage={
                query.isLoading ? "carregando…" : "nenhum usuário encontrado"
              }
            />
          </div>

          {query.data ? (
            <div className="mt-4">
              <Pagination
                page={page}
                perPage={PER_PAGE}
                total={query.data.meta.total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>

        {user ? (
          <CreateUserDialog
            open={creating}
            onOpenChange={setCreating}
            callerRole={user.role}
          />
        ) : null}

        {resetTarget ? (
          <ResetPasswordDialog
            open
            onOpenChange={(o) => {
              if (!o) setResetTarget(null);
            }}
            userId={resetTarget.id}
            userEmail={resetTarget.email}
          />
        ) : null}
      </AppLayout>
    </RequireAuth>
  );
}
